import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { AuthProvider, useAuth, useActiveOrgId } from './contexts/AuthContext';
import { DictsProvider } from './contexts/DictsContext';
import { UnifiedStatsProvider } from './contexts/UnifiedStatsContext';
import { WatchdogProvider } from './contexts/WatchdogContext';
import { Wazzup24Provider } from './contexts/Wazzup24Context';
import { SelfQrProvider } from './contexts/SelfQrContext';
import { WabaProvider } from './contexts/WabaContext';
import { TelegramProvider } from './contexts/TelegramContext';
import { InstagramProvider } from './contexts/InstagramContext';
import { TikTokProvider } from './contexts/TikTokContext';
import { SmartLombardProvider } from './contexts/SmartLombardContext';
import { VersionCheckProvider } from './contexts/VersionCheckContext';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { CourseViewRedirect, ModuleNewRedirect, ModuleEditRedirect } from './pages/AdminAcademy/AcademyAdminRedirects';
import ProtectedRoute from './components/ProtectedRoute';
import { AppFeaturesProvider } from './contexts/AppFeaturesContext';
import { OrgWorkflowProvider } from './contexts/OrgWorkflowContext';
import { AssignmentRolesProvider } from './contexts/AssignmentRolesContext';
import { TaskManagerDictsProvider } from './contexts/TaskManagerDictsContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { NotificationPreferencesProvider } from './contexts/NotificationPreferencesContext';
import { ServiceTypesProvider } from './contexts/ServiceTypesContext';
import { BrandingProvider, BrandingAuthBridge } from './contexts/BrandingContext';
import { SandboxSessionProvider } from './contexts/SandboxSessionContext';
import Layout from './components/Layout/Layout';
import './styles/global.css';
import RequirePermission from './components/Permissions/RequirePermission';
import RequireAdmin from './components/Permissions/RequireAdmin';
import RequireFeature from './components/Permissions/RequireFeature';
import RequireStorageAccess from './components/Permissions/RequireStorageAccess';
import SessionWarningModal from './components/Session/SessionWarningModal';
import { useAppFeatures } from './contexts/AppFeaturesContext';
import { usePermissions } from './utils/usePermissions';
import InAppPushHost from './components/Common/InAppPushHost';

// Lazy loading страниц
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SelectOrganizationPage = lazy(() => import('./pages/SelectOrganizationPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const AdminDictsPage = lazy(() => import('./pages/AdminDictsPage'));
const AdminDictItemsPage = lazy(() => import('./pages/AdminDictItemsPage'));
const CustomerDetailsPage = lazy(() => import('./pages/CustomerDetailsPage'));
const AdminGroupsPage = lazy(() => import('./pages/AdminGroupsPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminSecretTemplatesPage = lazy(() => import('./pages/AdminSecretTemplatesPage'));
const AdminUserDetailsPage = lazy(() => import('./pages/AdminUserDetailsPage'));
const AdminRolesPage = lazy(() => import('./pages/AdminRolesPage'));
const AdminRoleDetailsPage = lazy(() => import('./pages/AdminRoleDetailsPage'));
const AdminAgentMonitorPage = lazy(() => import('./pages/AdminAgentMonitorPage'));
const AdminAgentRunDetailPage = lazy(() => import('./pages/AdminAgentRunDetailPage'));
const AdminStagesPage = lazy(() => import('./pages/AdminStagesPage'));
const AdminActionsPage = lazy(() => import('./pages/AdminActionsPage'));
const AdminPresenceStatusesPage = lazy(() => import('./pages/AdminPresenceStatusesPage'));
const AdminScheduleSettingsPage = lazy(() => import('./pages/AdminScheduleSettingsPage'));
const AdminWorkSchedulePage = lazy(() => import('./pages/AdminWorkSchedulePage'));
const AdminApiKeysPage = lazy(() => import('./pages/AdminApiKeysPage'));
const TicketsPage = lazy(() => import('./pages/TicketsPage'));
const TicketDetailsPage = lazy(() => import('./pages/TicketDetailsPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const LeadDetailsPage = lazy(() => import('./pages/LeadDetailsPage'));
const LeadsRedirect = lazy(() => import('./components/Leads/LeadsRedirect'));
const SmartLombardPawnTicketsPage = lazy(() => import('./pages/SmartLombardPawnTicketsPage'));
const SmartLombardPawnTicketDetailsPage = lazy(() => import('./pages/SmartLombardPawnTicketDetailsPage'));
const SmartLombardRedirect = lazy(() => import('./components/SmartLombard/SmartLombardRedirect'));
const MessageTemplateGroupsPage = lazy(() => import('./pages/MessageTemplateGroupsPage'));
const MessageTemplatesPage = lazy(() => import('./pages/MessageTemplatesPage'));
const StoragePage = lazy(() => import('./pages/StoragePage'));
const StorageTemplateRecordsPage = lazy(() => import('./pages/StorageTemplateRecordsPage'));
const Wazzup24Page = lazy(() => import('./pages/Wazzup24Page'));
const SelfQrPage = lazy(() => import('./pages/SelfQrPage'));
const WabaPage = lazy(() => import('./pages/WabaPage'));
const WabaAnalyticsPage = lazy(() => import('./pages/WabaAnalyticsPage'));
const TelegramPage = lazy(() => import('./pages/TelegramPage'));
const InstagramPage = lazy(() => import('./pages/InstagramPage'));
const InstagramAdminPage = lazy(() => import('./pages/InstagramAdminPage'));
const InstagramCallbackPage = lazy(() => import('./pages/InstagramCallbackPage'));
const TikTokPage = lazy(() => import('./pages/TikTokPage'));
const TikTokAdminPage = lazy(() => import('./pages/TikTokAdminPage'));
const TikTokCallbackPage = lazy(() => import('./pages/TikTokCallbackPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const AdminWorkflowBuilderPage = lazy(() => import('./pages/AdminWorkflowBuilderPage'));
const AdminWorkflowAnalyticsPage = lazy(() => import('./pages/AdminWorkflowAnalyticsPage'));
const WorkMonitorLayout = lazy(() => import('./pages/WorkMonitorLayout'));
const WorkMonitorPage = lazy(() => import('./pages/WorkMonitorPage'));
const AgreementsPage = lazy(() => import('./pages/AgreementsPage'));
const AgreementDetailsPage = lazy(() => import('./pages/AgreementDetailsPage'));
const DocumentTemplateCategoriesPage = lazy(() => import('./pages/DocumentTemplateCategoriesPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const DocumentTemplateDetailsPage = lazy(() => import('./pages/DocumentTemplateDetailsPage'));
const AssessmentsPage = lazy(() => import('./pages/AssessmentsPage'));
const AssessmentNewPage = lazy(() => import('./pages/AssessmentNewPage'));
const AssessmentDetailsPage = lazy(() => import('./pages/AssessmentDetailsPage'));
const AssessmentEditPage = lazy(() => import('./pages/AssessmentEditPage'));
const AssessmentDictsPage = lazy(() => import('./pages/AssessmentDictsPage'));
const AssessmentDictItemsPage = lazy(() => import('./pages/AssessmentDictItemsPage'));
const GoldPage = lazy(() => import('./pages/GoldPage'));
const GoldNewPage = lazy(() => import('./pages/GoldNewPage'));
const GoldDetailsPage = lazy(() => import('./pages/GoldDetailsPage'));
const GoldEditPage = lazy(() => import('./pages/GoldEditPage'));
const GoldDictsPage = lazy(() => import('./pages/GoldDictsPage'));
const GoldDictItemsPage = lazy(() => import('./pages/GoldDictItemsPage'));
const CheckupsPage = lazy(() => import('./pages/CheckupsPage'));
const CheckupNewPage = lazy(() => import('./pages/CheckupNewPage'));
const CheckupDetailsPage = lazy(() => import('./pages/CheckupDetailsPage'));
const CheckupEditPage = lazy(() => import('./pages/CheckupEditPage'));
const CheckupDictsPage = lazy(() => import('./pages/CheckupDictsPage'));
const CheckupDictItemsPage = lazy(() => import('./pages/CheckupDictItemsPage'));
const PaymentSchedulesPage = lazy(() => import('./pages/PaymentSchedulesPage'));
const PaymentScheduleDetailsPage = lazy(() => import('./pages/PaymentScheduleDetailsPage'));
const DialerPage = lazy(() => import('./pages/DialerPage'));
const DialerCampaignDetailsPage = lazy(() => import('./pages/DialerCampaignDetailsPage'));
const DialerCampaignFormPage = lazy(() => import('./pages/DialerCampaignFormPage'));
const DialerJobDetailsPage = lazy(() => import('./pages/DialerJobDetailsPage'));
const AdminIntegrationsPage = lazy(() => import('./pages/AdminIntegrationsPage'));
const AdminEntityRelationsPage = lazy(() => import('./pages/AdminEntityRelationsPage'));
const AdminAllowedTablesPage = lazy(() => import('./pages/AdminAllowedTablesPage'));
const AdminUserStrategiesPage = lazy(() => import('./pages/AdminUserStrategiesPage'));
const AdminB2BPartnersPage = lazy(() => import('./pages/AdminB2BPartnersPage'));
const AdminB2BPartnerDetailsPage = lazy(() => import('./pages/AdminB2BPartnerDetailsPage'));
const AgentSkillsPage = lazy(() => import('./pages/AgentSkillsPage'));
const AgentSkillDetailPage = lazy(() => import('./pages/AgentSkillDetailPage'));
const AgentsListPage = lazy(() => import('./pages/AgentsListPage'));
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage'));
const AgentSessionsBoardPage = lazy(() => import('./pages/AgentSessionsBoardPage'));
const SessionDetailPage = lazy(() => import('./pages/SessionDetailPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SupportDetailsPage = lazy(() => import('./pages/SupportDetailsPage'));
const SupportReportsPage = lazy(() => import('./pages/SupportReportsPage'));
const EntityListPage = lazy(() => import('./pages/EntityListPage'));
const EntityDetailPage = lazy(() => import('./pages/EntityDetailPage'));
const ServiceManagementPage = lazy(() => import('./pages/ServiceManagementPage'));
const ServiceSettingsPage = lazy(() => import('./pages/ServiceSettingsPage'));
const AdminOrganizationsPage = lazy(() => import('./pages/AdminOrganizationsPage'));
const AdminOrganizationDetailsPage = lazy(() => import('./pages/AdminOrganizationDetailsPage'));
const AdminBrandingPage = lazy(() => import('./pages/AdminBrandingPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const EmailsPage = lazy(() => import('./pages/EmailsPage'));
// Testing / Certification — candidate pages (fullscreen layout)
const TestExamLayout = lazy(() => import('./components/TestExam/TestExamLayout'));
const TestStartPage = lazy(() => import('./components/TestExam/TestStartPage'));
const TestExamPage = lazy(() => import('./components/TestExam/TestExamPage'));
const TestResultPage = lazy(() => import('./components/TestExam/TestResultPage'));
const TestCatalogPage = lazy(() => import('./components/TestExam/TestCatalogPage'));
// Testing / Certification — admin pages (standalone module, no entity-engine)
const TestTemplatesPage = lazy(() => import('./pages/Testing/TestTemplatesPage'));
const TestTemplateDetailsPage = lazy(() => import('./pages/Testing/TestTemplateDetailsPage'));
const TestTemplateWizardPage = lazy(() => import('./pages/Testing/TestTemplateWizardPage'));
// AB3 Academy — learner UI (Phase B): каталог, деталь, плеер, результат, completed.
const AcademyCoursesPage = lazy(() => import('./pages/Academy/AcademyCoursesPage'));
const AcademyCourseDetailPage = lazy(() => import('./pages/Academy/AcademyCourseDetailPage'));
const AcademyModulePlayerPage = lazy(() => import('./pages/Academy/AcademyModulePlayerPage'));
const AcademyVerifyResultPage = lazy(() => import('./pages/Academy/AcademyVerifyResultPage'));
const AcademyCourseCompletedPage = lazy(() => import('./pages/Academy/AcademyCourseCompletedPage'));
// AB3 Academy — admin UI (Phase C / C.5-C.6).
const AdminAcademyCoursesPage = lazy(() => import('./pages/AdminAcademy/AdminAcademyCoursesPage'));
const AdminAcademyCourseWorkspacePage = lazy(() => import('./pages/AdminAcademy/AdminAcademyCourseWorkspacePage'));
const TestHistoryPage = lazy(() => import('./pages/Testing/TestHistoryPage'));
// CpaChannelsPage lazy-импортируется внутри AdminIntegrationsPage
// Группы/Роли/Доступ/Пользователи удалены из навигации

// Bridge: connect AuthContext active organization → BrandingContext
const BrandingBridge: React.FC = () => {
  const orgId = useActiveOrgId();
  return <BrandingAuthBridge orgId={orgId} />;
};

const TaskIdRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/tasks?taskId=${id}` : '/tasks'} replace />;
};

// Компонент загрузки
const PageLoader: React.FC = () => (
  <div className="flex justify-center items-center text-gray-500" style={{ height: '100vh', fontSize: '18px' }}>
    Загрузка...
  </div>
);

// Gate для /emails: admin байпасит, иначе — app_features.email + permission
const EmailsAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const features = useAppFeatures();
  const { hasPermission } = usePermissions();
  const allowed = profile?.is_admin === true
    || (features.email && hasPermission('emails:show_module'));
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
};

const App: React.FC = () => {
  return (
    <VersionCheckProvider>
      <ErrorBoundary>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <NuqsAdapter>
          <BrandingProvider>
          <AuthProvider>
            <BrandingBridge />
            <DictsProvider>
              <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/select-organization" element={<SelectOrganizationPage />} />

              {/* Testing / Certification — candidate fullscreen (no sidebar) */}
              <Route
                path="/exam"
                element={
                  <ProtectedRoute>
                    <TestExamLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="start/:templateId" element={<TestStartPage />} />
                <Route path=":attemptId" element={<TestExamPage />} />
                <Route path=":attemptId/result" element={<TestResultPage />} />
              </Route>

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppFeaturesProvider>
                      <SandboxSessionProvider>
                      <OrgWorkflowProvider>
                      <AssignmentRolesProvider>
                      <TaskManagerDictsProvider>
                      <NotificationPreferencesProvider>
                        <NotificationsProvider>
                          <ServiceTypesProvider>
                            <Wazzup24Provider>
                              <SelfQrProvider>
                                <WabaProvider>
                                <TelegramProvider>
                                <InstagramProvider>
                                <TikTokProvider>
                                <SmartLombardProvider>
                                  <UnifiedStatsProvider>
                                    <WatchdogProvider>
                                      <Layout />
                                      <InAppPushHost />
                                    </WatchdogProvider>
                                  </UnifiedStatsProvider>
                                </SmartLombardProvider>
                                </TikTokProvider>
                                </InstagramProvider>
                                </TelegramProvider>
                                </WabaProvider>
                              </SelfQrProvider>
                            </Wazzup24Provider>
                          </ServiceTypesProvider>
                        </NotificationsProvider>
                      </NotificationPreferencesProvider>
                      </TaskManagerDictsProvider>
                      </AssignmentRolesProvider>
                      </OrgWorkflowProvider>
                      </SandboxSessionProvider>
                    </AppFeaturesProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="leads" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <LeadsRedirect />
                  </RequireFeature>
                } />
                <Route path="leads/new" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="leads:view:new" fallback={<Navigate to="/" replace />}>
                      <LeadsPage view="new" />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="leads/assigned" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="leads:view:assigned" fallback={<Navigate to="/" replace />}>
                      <LeadsPage view="assigned" />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="leads/my" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="leads:view:mine" fallback={<Navigate to="/" replace />}>
                      <LeadsPage view="my" />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="leads/all" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="leads:view:all" fallback={<Navigate to="/" replace />}>
                      <LeadsPage view="all" />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="leads/:id" element={
                  <RequireFeature name="leads" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code={"leads:read"} fallback={<Navigate to="/" replace />}>
                      <LeadDetailsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="customers" element={
                  <RequirePermission code="customers:list" fallback={<Navigate to="/" replace />}>
                    <CustomersPage />
                  </RequirePermission>
                } />
                <Route path="customers/:id" element={
                  <RequirePermission code={"customers:read"} fallback={<Navigate to="/" replace />}>
                    <CustomerDetailsPage />
                  </RequirePermission>
                } />
                <Route path="agreements" element={
                  <RequirePermission code="agreements:show_module" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="agreements:list" fallback={<Navigate to="/" replace />}>
                      <AgreementsPage />
                    </RequirePermission>
                  </RequirePermission>
                } />
                <Route path="agreements/:id" element={
                  <RequirePermission code="agreements:show_module" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="agreements:read" fallback={<Navigate to="/" replace />}>
                      <AgreementDetailsPage />
                    </RequirePermission>
                  </RequirePermission>
                } />
                {/* Payment Schedules (Графики платежей) */}
                <Route path="payment-schedules" element={
                  <RequireFeature name="payment_schedule" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="payment_schedule:show_module" fallback={<Navigate to="/" replace />}>
                      <PaymentSchedulesPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="payment-schedules/:id" element={
                  <RequireFeature name="payment_schedule" fallback={<Navigate to="/" replace />}>
                    <PaymentScheduleDetailsPage />
                  </RequireFeature>
                } />
                {/* Assessments (Оценки смартфонов) */}
                <Route path="assessments" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:list" fallback={<Navigate to="/" replace />}>
                        <AssessmentsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="assessments/new" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:create" fallback={<Navigate to="/" replace />}>
                        <AssessmentNewPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="assessments/dicts" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:list" fallback={<Navigate to="/" replace />}>
                        <AssessmentDictsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="assessments/dicts/:code" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:list" fallback={<Navigate to="/" replace />}>
                        <AssessmentDictItemsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="assessments/:id" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:read" fallback={<Navigate to="/" replace />}>
                        <AssessmentDetailsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="assessments/:id/edit" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="assessments:update" fallback={<Navigate to="/" replace />}>
                        <AssessmentEditPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Checkups (Чек-ап) */}
                <Route path="checkups" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <CheckupsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="checkups/new" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <CheckupNewPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="checkups/dicts" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="checkups:dicts:manage" fallback={<Navigate to="/" replace />}>
                        <CheckupDictsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="checkups/dicts/:code" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="checkups:dicts:manage" fallback={<Navigate to="/" replace />}>
                        <CheckupDictItemsPage />
                      </RequirePermission>
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="checkups/:id" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <CheckupDetailsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="checkups/:id/edit" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="assessment:show_module" fallback={<Navigate to="/" replace />}>
                      <CheckupEditPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Gold (Золото — микрокредит под залог золота) */}
                <Route path="gold" element={
                  <RequireFeature name="assessment" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="gold:show_module" fallback={<Navigate to="/" replace />}>
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<GoldPage />} />
                  <Route path="new" element={<GoldNewPage />} />
                  <Route path="dicts" element={<GoldDictsPage />} />
                  <Route path="dicts/:code" element={<GoldDictItemsPage />} />
                  <Route path=":id" element={<GoldDetailsPage />} />
                  <Route path=":id/edit" element={<GoldEditPage />} />
                </Route>
                <Route path="storage" element={
                  <RequireFeature name="secret" fallback={<Navigate to="/" replace />}>
                    <RequireStorageAccess fallback={<Navigate to="/" replace />}>
                      <StoragePage />
                    </RequireStorageAccess>
                  </RequireFeature>
                } />
                <Route path="storage/:code" element={
                  <RequireFeature name="secret" fallback={<Navigate to="/" replace />}>
                    <RequireStorageAccess fallback={<Navigate to="/" replace />}>
                      <StorageTemplateRecordsPage />
                    </RequireStorageAccess>
                  </RequireFeature>
                } />
                {/* Email письма */}
                <Route path="emails" element={
                  <EmailsAccessGate>
                    <EmailsPage />
                  </EmailsAccessGate>
                } />
                {/* Support (доступен всем) */}
                <Route path="support" element={<SupportPage view="my" />} />
                <Route path="support/:id" element={<SupportDetailsPage />} />
                <Route path="admin/support" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <SupportPage view="admin" />
                  </RequireAdmin>
                } />
                <Route path="admin/support/reports" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <SupportReportsPage />
                  </RequireAdmin>
                } />
                {/* Import */}
                <Route path="import" element={
                  <RequireFeature name="import_data" fallback={<Navigate to="/" replace />}>
                    <ImportPage />
                  </RequireFeature>
                } />
                {/* Tasks (WorkflowKit) — детали открываются через drawer (?taskId=N) */}
                <Route path="tasks" element={<TasksPage />} />
                <Route path="tasks/:id" element={<TaskIdRedirect />} />
                {/* Admin Workflow (WorkflowKit) */}
                <Route path="admin/workflow" element={
                  <RequirePermission code="tasks:admin_config" fallback={<Navigate to="/" replace />}>
                    <AdminWorkflowBuilderPage />
                  </RequirePermission>
                } />
                <Route path="admin/workflow-analytics" element={
                  <RequirePermission code="tasks:admin_config" fallback={<Navigate to="/" replace />}>
                    <AdminWorkflowAnalyticsPage />
                  </RequirePermission>
                } />
                {/* SmartLombard */}
                <Route path="smartlombard" element={
                  <RequireFeature name="smartlombard" fallback={<Navigate to="/" replace />}>
                    <Outlet />
                  </RequireFeature>
                }>
                  <Route index element={<SmartLombardRedirect />} />
                  <Route path="pawn-tickets" element={<SmartLombardRedirect />} />
                  <Route path=":instanceId/pawn-tickets" element={<SmartLombardPawnTicketsPage />} />
                  <Route path=":instanceId/pawn-tickets/:id" element={<SmartLombardPawnTicketDetailsPage />} />
                </Route>
                {/* Wazzup24 */}
                <Route path="wazzup24" element={
                  <RequireFeature name="wazzup" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="integrations:wazzup24:show_module" fallback={<Navigate to="/" replace />}>
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<Wazzup24Page />} />
                  <Route path=":instanceId" element={<Wazzup24Page />} />
                </Route>
                {/* Self QR — собственный WhatsApp-провайдер через Baileys sidecar */}
                <Route path="self-qr" element={
                  <RequireFeature name="self_qr" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="integrations:self_qr:show_module" fallback={<Navigate to="/" replace />}>
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<SelfQrPage />} />
                  <Route path=":instanceId" element={<SelfQrPage />} />
                  <Route path=":instanceId/connect" element={<SelfQrPage initialModal="connect" />} />
                </Route>
                {/* WABA — официальный WhatsApp Business через Meta Cloud API */}
                <Route path="waba" element={
                  <RequireFeature name="waba" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="integrations:waba:show_module" fallback={<Navigate to="/" replace />}>
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<WabaPage />} />
                  <Route path=":instanceId" element={<WabaPage />} />
                  <Route path=":instanceId/analytics" element={<WabaAnalyticsPage />} />
                </Route>
                {/* Telegram — личный аккаунт через MTProto */}
                <Route path="telegram" element={
                  <RequireFeature name="telegram" fallback={<Navigate to="/" replace />}>
                    <RequirePermission
                      codes={['integrations:telegram:show_module', 'integrations:telegram:read']}
                      fallback={<Navigate to="/" replace />}
                    >
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<TelegramPage />} />
                  <Route path=":instanceId" element={<TelegramPage />} />
                </Route>
                {/* Instagram — Meta (Instagram Login / Graph API) */}
                {/* Инбокс: операторский Direct-чат. */}
                <Route path="instagram" element={
                  <RequireFeature name="instagram" fallback={<Navigate to="/" replace />}>
                    <RequirePermission
                      codes={['integrations:instagram:show_module', 'integrations:instagram:read']}
                      fallback={<Navigate to="/" replace />}
                    >
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<InstagramPage />} />
                  <Route path=":instanceId" element={<InstagramPage />} />
                </Route>
                {/* Админ-страница: настройка Meta App + подключение аккаунтов (достижима до первого аккаунта). */}
                <Route path="integrations/instagram/admin" element={
                  <RequireFeature name="instagram" fallback={<Navigate to="/" replace />}>
                    <RequirePermission
                      codes={['integrations:instagram:show_module', 'integrations:instagram:read']}
                      fallback={<Navigate to="/" replace />}
                    >
                      <InstagramAdminPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* OAuth callback: точка возврата после Meta. НЕ гейтим фичей —
                    редирект из Meta должен дойти и показать результат; достаточно auth
                    (родительский ProtectedRoute). Внутренние вызовы 403 — отдельно. */}
                <Route path="integrations/instagram/callback" element={<InstagramCallbackPage />} />
                {/* TikTok — TikTok Direct (Business Messaging) */}
                {/* Инбокс: операторский Direct-чат. */}
                <Route path="tiktok" element={
                  <RequireFeature name="tiktok" fallback={<Navigate to="/" replace />}>
                    <RequirePermission
                      codes={['integrations:tiktok:show_module', 'integrations:tiktok:read']}
                      fallback={<Navigate to="/" replace />}
                    >
                      <Outlet />
                    </RequirePermission>
                  </RequireFeature>
                }>
                  <Route index element={<TikTokPage />} />
                  <Route path=":instanceId" element={<TikTokPage />} />
                </Route>
                {/* Админ-страница: настройка TikTok App + подключение аккаунтов. */}
                <Route path="integrations/tiktok/admin" element={
                  <RequireFeature name="tiktok" fallback={<Navigate to="/" replace />}>
                    <RequirePermission
                      codes={['integrations:tiktok:show_module', 'integrations:tiktok:read']}
                      fallback={<Navigate to="/" replace />}
                    >
                      <TikTokAdminPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* OAuth callback: точка возврата после TikTok. НЕ гейтим фичей —
                    редирект из TikTok должен дойти и показать результат; достаточно auth
                    (родительский ProtectedRoute). Внутренние вызовы 403 — отдельно. */}
                <Route path="integrations/tiktok/callback" element={<TikTokCallbackPage />} />
                {/* Tickets */}
                <Route path="tickets" element={
                  <RequireFeature name="tickets" fallback={<Navigate to="/" replace />}>
                    <Navigate to="/tickets/new" replace />
                  </RequireFeature>
                } />
                <Route path="tickets/:view" element={
                  <RequireFeature name="tickets" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code={"tickets:list"} fallback={<Navigate to="/" replace />}>
                      <TicketsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="ticket/:id" element={
                  <RequireFeature name="tickets" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code={"tickets:read"} fallback={<Navigate to="/" replace />}>
                      <TicketDetailsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Удалены: groups, roles, permissions, users */}
                <Route path="admin/dicts" element={
                  <RequirePermission code="dicts:registry:list" fallback={<Navigate to="/" replace />}>
                    <AdminDictsPage />
                  </RequirePermission>
                } />
                <Route path="admin/document-template-categories" element={
                  <RequirePermission code="document_templates:categories:list" fallback={<Navigate to="/" replace />}>
                    <DocumentTemplateCategoriesPage />
                  </RequirePermission>
                } />
                <Route path="admin/document-template-categories/:categoryId/templates" element={
                  <RequirePermission code="document_templates:categories:read" fallback={<Navigate to="/" replace />}>
                    <DocumentTemplatesPage />
                  </RequirePermission>
                } />
                <Route path="admin/document-templates/:id" element={
                  <RequirePermission code="document_templates:categories:read" fallback={<Navigate to="/" replace />}>
                    <DocumentTemplateDetailsPage />
                  </RequirePermission>
                } />
                <Route path="admin/secret-templates" element={
                  <RequirePermission code="secrets:templates:list" fallback={<Navigate to="/" replace />}>
                    <AdminSecretTemplatesPage />
                  </RequirePermission>
                } />
                <Route path="admin/dicts/:code" element={
                  <RequirePermission code="dicts:items:list" fallback={<Navigate to="/" replace />}>
                    <AdminDictItemsPage />
                  </RequirePermission>
                } />
                <Route path="admin/groups" element={
                  <RequirePermission code="groups:list" fallback={<Navigate to="/" replace />}>
                    <AdminGroupsPage />
                  </RequirePermission>
                } />
                <Route path="admin/message-template-groups" element={
                  <RequirePermission code="templates:groups:list" fallback={<Navigate to="/" replace />}>
                    <MessageTemplateGroupsPage />
                  </RequirePermission>
                } />
                <Route path="admin/message-template-groups/:id/templates" element={
                  <RequirePermission code="templates:groups:read" fallback={<Navigate to="/" replace />}>
                    <MessageTemplatesPage />
                  </RequirePermission>
                } />
                <Route path="admin/roles" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="rbac:roles:list" fallback={<Navigate to="/" replace />}>
                      <AdminRolesPage />
                    </RequirePermission>
                  </RequireAdmin>
                } />
                <Route path="admin/roles/:id" element={
                  <RequirePermission code="rbac:roles:read" fallback={<Navigate to="/" replace />}>
                    <AdminRoleDetailsPage />
                  </RequirePermission>
                } />
                {/* Agent Run Monitor — admin-only, cross-org */}
                <Route path="admin/agent-monitor" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminAgentMonitorPage />
                  </RequireAdmin>
                } />
                <Route path="admin/agent-monitor/:runId" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminAgentRunDetailPage />
                  </RequireAdmin>
                } />
                {/* Мир 2 — ИИ-Агенты: CRUD + доска сессий + живой диалог (RequireFeature ai_agent + RequireAdmin) */}
                <Route path="agents" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <AgentsListPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                <Route path="agents/board" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <AgentSessionsBoardPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                <Route path="agents/sessions/:sessionId" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <SessionDetailPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                <Route path="agents/:id" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <AgentDetailPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                <Route path="admin/stages" element={
                  <RequirePermission code="stages:list" fallback={<Navigate to="/" replace />}>
                    <AdminStagesPage />
                  </RequirePermission>
                } />
                <Route path="admin/actions" element={
                  <RequirePermission code="actions:list" fallback={<Navigate to="/" replace />}>
                    <AdminActionsPage />
                  </RequirePermission>
                } />
                <Route path="admin/presence-statuses" element={
                  <RequirePermission code="presence-statuses:list" fallback={<Navigate to="/" replace />}>
                    <AdminPresenceStatusesPage />
                  </RequirePermission>
                } />
                {/* Work Monitor (Watchdog) */}
                <Route path="admin/work-monitor" element={
                  <RequireFeature name="shift" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <WorkMonitorLayout />
                    </RequireAdmin>
                  </RequireFeature>
                }>
                  <Route index element={<WorkMonitorPage />} />
                </Route>
                <Route path="admin/work-schedule" element={
                  <RequireFeature name="shift" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="scheduler:work_schedule" fallback={<Navigate to="/" replace />}>
                      <AdminWorkSchedulePage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/schedule-settings" element={
                  <RequireFeature name="shift" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="scheduler:settings" fallback={<Navigate to="/" replace />}>
                      <AdminScheduleSettingsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/users" element={
                  <RequirePermission code="users:list" fallback={<Navigate to="/" replace />}>
                    <AdminUsersPage />
                  </RequirePermission>
                } />
                <Route path="admin/users/:id" element={
                  <RequirePermission code="users:read" fallback={<Navigate to="/" replace />}>
                    <AdminUserDetailsPage />
                  </RequirePermission>
                } />
                {/* Organizations (superadmin only) */}
                <Route path="admin/organizations" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminOrganizationsPage />
                  </RequireAdmin>
                } />
                <Route path="admin/organizations/:id" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminOrganizationDetailsPage />
                  </RequireAdmin>
                } />
                {/* Branding (White-Label) */}
                <Route path="admin/branding" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminBrandingPage />
                  </RequireAdmin>
                } />
                {/* API Keys */}
                <Route path="admin/api-keys" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminApiKeysPage />
                  </RequireAdmin>
                } />
                {/* B2B Partners */}
                <Route path="admin/b2b" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminB2BPartnersPage />
                  </RequireAdmin>
                } />
                <Route path="admin/b2b/:id" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminB2BPartnerDetailsPage />
                  </RequireAdmin>
                } />
                {/* AI Agent Skills */}
                <Route path="agent/skills" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <AgentSkillsPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                <Route path="agent/skills/:id" element={
                  <RequireFeature name="ai_agent" fallback={<Navigate to="/" replace />}>
                    <RequireAdmin fallback={<Navigate to="/" replace />}>
                      <AgentSkillDetailPage />
                    </RequireAdmin>
                  </RequireFeature>
                } />
                {/* Integration Admin (Управление интеграциями) */}
                <Route path="admin/integrations" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminIntegrationsPage />
                  </RequireAdmin>
                } />
                {/* CPA перенесён внутрь /admin/integrations?tab=cpa */}
                {/* Entity Relations (Связи сущностей) */}
                <Route path="admin/entity-relations" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminEntityRelationsPage />
                  </RequireAdmin>
                } />
                {/* Allowed Tables (Разрешённые таблицы) */}
                <Route path="admin/allowed-tables" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminAllowedTablesPage />
                  </RequireAdmin>
                } />
                {/* User Strategies (Пользовательские стратегии) */}
                <Route path="admin/user-strategies" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminUserStrategiesPage />
                  </RequireAdmin>
                } />
                <Route path="admin/user-strategies/:strategyId" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <AdminUserStrategiesPage />
                  </RequireAdmin>
                } />
                {/* Dialer (Автодайлер) */}
                <Route path="admin/dialer" element={
                  <RequireFeature name="agreement" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="dialer:campaigns:list" fallback={<Navigate to="/" replace />}>
                      <DialerPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/dialer/create" element={
                  <RequireFeature name="agreement" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="dialer:campaigns:create" fallback={<Navigate to="/" replace />}>
                      <DialerCampaignFormPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/dialer/:id" element={
                  <RequireFeature name="agreement" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="dialer:campaigns:read" fallback={<Navigate to="/" replace />}>
                      <DialerCampaignDetailsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/dialer/:id/edit" element={
                  <RequireFeature name="agreement" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="dialer:campaigns:update" fallback={<Navigate to="/" replace />}>
                      <DialerCampaignFormPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Dialer Jobs (детальная страница задачи) */}
                <Route path="admin/dialer/jobs/:jobId" element={
                  <RequireFeature name="agreement" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="dialer:jobs:read" fallback={<Navigate to="/" replace />}>
                      <DialerJobDetailsPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Тестирование — каталог доступных тестов */}
                <Route path="testing" element={
                  <RequireFeature name="testing" fallback={<Navigate to="/" replace />}>
                    <TestCatalogPage />
                  </RequireFeature>
                } />
                <Route path="testing/history" element={
                  <RequireFeature name="testing" fallback={<Navigate to="/" replace />}>
                    <TestHistoryPage />
                  </RequireFeature>
                } />

                {/* AB3 Academy — Phase B learner UI */}
                <Route path="academy" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:courses:read" fallback={<Navigate to="/" replace />}>
                      <AcademyCoursesPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="academy/courses/:id" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:courses:read" fallback={<Navigate to="/" replace />}>
                      <AcademyCourseDetailPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="academy/enrollments/:eid/modules/:mid" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:enrollments:read" fallback={<Navigate to="/" replace />}>
                      <AcademyModulePlayerPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="academy/enrollments/:eid/result/:mid" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:enrollments:read" fallback={<Navigate to="/" replace />}>
                      <AcademyVerifyResultPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="academy/enrollments/:eid/done" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:enrollments:read" fallback={<Navigate to="/" replace />}>
                      <AcademyCourseCompletedPage />
                    </RequirePermission>
                  </RequireFeature>
                } />

                {/* AB3 Academy — admin (Lane C) */}
                <Route path="admin/academy/courses" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:courses:write" fallback={<Navigate to="/" replace />}>
                      <AdminAcademyCoursesPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/academy/courses/new" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:courses:write" fallback={<Navigate to="/" replace />}>
                      <AdminAcademyCourseWorkspacePage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                <Route path="admin/academy/courses/:id" element={
                  <RequireFeature name="learning" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="learning:courses:write" fallback={<Navigate to="/" replace />}>
                      <AdminAcademyCourseWorkspacePage />
                    </RequirePermission>
                  </RequireFeature>
                } />
                {/* Старые роуты → редирект на единый workspace */}
                <Route path="admin/academy/courses/:id/view" element={<CourseViewRedirect />} />
                <Route path="admin/academy/courses/:courseId/modules/new" element={<ModuleNewRedirect />} />
                <Route path="admin/academy/courses/:courseId/modules/:moduleId/edit" element={<ModuleEditRedirect />} />

                {/* Testing Admin — standalone module */}
                <Route path="admin/test-templates" element={
                  <RequirePermission code="testing:templates:write" allowAdmin fallback={<Navigate to="/" replace />}>
                    <TestTemplatesPage />
                  </RequirePermission>
                } />
                <Route path="admin/test-templates/new" element={
                  <RequirePermission code="testing:templates:write" allowAdmin fallback={<Navigate to="/" replace />}>
                    <TestTemplateWizardPage />
                  </RequirePermission>
                } />
                <Route path="admin/test-templates/:id" element={
                  <RequirePermission code="testing:templates:write" allowAdmin fallback={<Navigate to="/" replace />}>
                    <TestTemplateDetailsPage />
                  </RequirePermission>
                } />
                <Route path="admin/test-templates/:id/edit" element={
                  <RequirePermission code="testing:templates:write" allowAdmin fallback={<Navigate to="/" replace />}>
                    <TestTemplateWizardPage />
                  </RequirePermission>
                } />

                {/* Entity Engine — Конструктор сервисов */}
                <Route path="entities/services" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <ServiceManagementPage />
                  </RequireAdmin>
                } />
                <Route path="entities/services/:code/settings" element={
                  <RequireAdmin fallback={<Navigate to="/" replace />}>
                    <ServiceSettingsPage />
                  </RequireAdmin>
                } />
                <Route path="entities/:code" element={<EntityListPage />} />
                <Route path="entities/:code/:id" element={<EntityDetailPage />} />

                {/* Notifications inbox */}
                <Route path="notifications" element={
                  <Suspense fallback={<PageLoader />}>
                    <RequireFeature name="notifications" fallback={<Navigate to="/" replace />}>
                      <RequirePermission code="notifications:read" fallback={<Navigate to="/" replace />}>
                        <NotificationsPage />
                      </RequirePermission>
                    </RequireFeature>
                  </Suspense>
                } />

                {/* Settings */}
                <Route path="settings/notifications" element={
                  <RequireFeature name="notifications" fallback={<Navigate to="/" replace />}>
                    <RequirePermission code="notifications:read" fallback={<Navigate to="/" replace />}>
                      <NotificationPreferencesPage />
                    </RequirePermission>
                  </RequireFeature>
                } />
              </Route>
              
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ErrorBoundary>
            </Suspense>
          </DictsProvider>
          {/* Модальное окно предупреждения о скором истечении сессии */}
          <SessionWarningModal />
          </AuthProvider>
          </BrandingProvider>
        </NuqsAdapter>
      </Router>
    </ErrorBoundary>
    </VersionCheckProvider>
  );
};

export default App;
